/**
 * Cutting a shape in half with a straight line.
 *
 * The fraction toy draws a line across a shape and the shape comes apart along
 * it, so the two pieces have to be real polygons with real areas — "a half" is
 * believable only when the app can show that the piece really is half the area
 * it came from.
 *
 * Bodies in this app are convex hulls, and a straight cut through a convex
 * polygon always yields exactly two convex pieces, so this walks the outline
 * once and hands each edge to whichever side it belongs on.
 */

import type { Vec } from './vec.ts';

/** Signed distance from the line through a→b; sign tells you which side. */
function side(a: Vec, b: Vec, p: Vec): number {
  return (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
}

function lerp(p: Vec, q: Vec, t: number): Vec {
  return { x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t };
}

/**
 * Split `poly` along the infinite line through a and b. Returns the two pieces,
 * or null when the line misses the shape (or only grazes a corner) — a cut that
 * produces one piece and a sliver of nothing is not a cut.
 */
export function splitPolygon(poly: Vec[], a: Vec, b: Vec): [Vec[], Vec[]] | null {
  if (poly.length < 3) return null;

  const left: Vec[] = [];
  const right: Vec[] = [];
  const EPS = 1e-9;

  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    const dp = side(a, b, p);
    const dq = side(a, b, q);

    if (dp >= -EPS) left.push(p);
    if (dp <= EPS) right.push(p);

    // The edge crosses the line: both pieces get the crossing point.
    if ((dp > EPS && dq < -EPS) || (dp < -EPS && dq > EPS)) {
      const t = dp / (dp - dq);
      const cut = lerp(p, q, t);
      left.push(cut);
      right.push(cut);
    }
  }

  if (left.length < 3 || right.length < 3) return null;
  return [left, right];
}
